import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { values } from 'lodash';
import { Section, Grid, Col, Avatar } from '@fiverr-private/fit';
import { TabGroup, Tab } from '@fiverr-private/orca';
import { I18n } from '@fiverr-private/i18n-react';
import {
    useScrollToTabByQueryParam,
    useWorkExperience,
    ProfileBadgeRatings,
    ProUserInfoContainer,
    SafeWrapper,
    getWorldCupWinnerBadgeImageUrl,
} from '@fiverr-private/seller_pages_toolbox';
import { Stack } from '@fiverr-private/layout_components';
import { useRolloutContext } from '../../../contexts/RolloutContext';
import { useMonitoring, SOURCES } from '../../../monitoring';
import { ButtonsGroup, ContactButton, DotsMenuButton } from '../../common/ActionButtons';
import { RespondTimeInfo } from '../../common/Info';
import { useFreelancerPageContext } from '../../FreelancerPageContext';
import { usePortfolio } from '../FreelancerPortfolio';
import { ProAvatar } from '../../common/ProAvatar';
import { useScrollObserve } from './useScrollObserve';
import { TabName, scrollToTab } from './tab-names';

import styles from './index.module.scss';
import './orca-tabs.scss';

const T_PREFIX = 'freelancer_page.sticky_header';

const OFFSET = 20;

/** @type {(headerRef: React.RefObject<HTMLDivElement>) => number} */
const getHeaderHeight = (headerRef) => {
    const height = headerRef.current ? headerRef.current.children[0]?.getBoundingClientRect().height : 0;
    return height ? height + OFFSET : 0;
};

/** @param {boolean} condition */
const withHiddenClass = (condition) => (condition ? 'hidden' : '');

/** @type {import('./index').StickyHeader} */

const StickyHeader = ({ seller, sellerTimeZone, isOnline, observeHideRef }) => {
    /** @type {React.RefObject<HTMLDivElement>} */
    const headerRef = useRef(null);
    const [shouldShow, setShouldShow] = useState(!observeHideRef);
    const [activeTab, setActiveTab] = useState(TabName.About);
    const { Providers, fireEvents } = useMonitoring();
    const {
        inQualasNewLevelSystemRollout,
        inQualasWorldCupWinnerArgentinaRollout,
        inQualasWorldCupWinnerSpainRollout,
    } = useRolloutContext();
    const { isShowLearnModal, isLoggedIn, hasReviews, hasTestimonials, isExperiential } = useFreelancerPageContext();
    const { showPortfolio } = usePortfolio();
    const { showWorkExperience } = useWorkExperience();

    const { user, isPro } = seller;
    const worldCupWinnerBadgeImageUrl = getWorldCupWinnerBadgeImageUrl({
        countryCode: user?.address?.countryCode,
        isArgentinaWorldCupWinnerRolloutActive: inQualasWorldCupWinnerArgentinaRollout,
        isSpainWorldCupWinnerRolloutActive: inQualasWorldCupWinnerSpainRollout,
    });

    const tabNames = useMemo(() => values(TabName), []);
    const onTabIntersect = useCallback(
        (currentTab) => {
            if (currentTab && currentTab !== activeTab) {
                setActiveTab(currentTab);
            }
        },
        [activeTab]
    );

    const onTabClickBI = (tabName) => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'clicked on anchors',
                'anchor tab name': tabName,
            },
        });
    };

    /** @type {(idx: number|undefined, tabName: keyof TabName) => void} */
    const onSwitch = useCallback(
        (idx, tabName) => {
            onTabClickBI(tabName);
            const offset = getHeaderHeight(headerRef);
            scrollToTab(tabName, -offset);
        },
        [headerRef]
    );

    const onRatingsClick = useCallback(() => {
        onSwitch(undefined, TabName.Reviews);
    }, [onSwitch]);

    // Observe id element, and scroll position to update active tab automatically
    useScrollObserve({
        shouldShow,
        elementIds: tabNames,
        headerHeight: getHeaderHeight(headerRef),
        onTabIntersect,
    });

    // Observe banner, and show sticky header if banner out of port view
    useEffect(() => {
        if (!observeHideRef || !observeHideRef.current) {
            return;
        }
        const observer = new IntersectionObserver(([bannerEl]) => {
            const { isIntersecting } = bannerEl;
            setShouldShow(!isIntersecting);
        }, {});

        observer.observe(observeHideRef.current);
        return () => observer.disconnect();
    }, [observeHideRef]);

    const headerClasses = {
        [styles.activeHeader]: shouldShow,
    };

    const containerClasses = {
        hidden: isShowLearnModal || !shouldShow,
    };

    useScrollToTabByQueryParam(-getHeaderHeight(headerRef));

    const experientialTabGroup = (
        <TabGroup key={activeTab} activeTab={activeTab} onSwitch={onSwitch}>
            <Tab title={<I18n k={`${T_PREFIX}.tabs.about`} />} name={TabName.About} />
            <Tab title={<I18n k={`${T_PREFIX}.tabs.services`} />} name={TabName.Services} />
            <Tab
                className={withHiddenClass(!showPortfolio)}
                title={<I18n k={`${T_PREFIX}.tabs.portfolio`} />}
                name={TabName.Portfolio}
            />
            <Tab
                className={withHiddenClass(!showWorkExperience)}
                title={<I18n k={`${T_PREFIX}.tabs.work_experience`} />}
                name={TabName.WorkExperience}
            />
            <Tab
                className={withHiddenClass(!hasReviews)}
                title={<I18n k={`${T_PREFIX}.tabs.reviews`} />}
                name={TabName.Reviews}
            />
            <Tab
                className={withHiddenClass(!hasTestimonials)}
                title={<I18n k={`${T_PREFIX}.tabs.recommendations`} />}
                name={TabName.Testimonials}
            />
        </TabGroup>
    );

    const nonExperientialTabGroup = (
        <TabGroup key={activeTab} activeTab={activeTab} onSwitch={onSwitch}>
            <Tab title={<I18n k={`${T_PREFIX}.tabs.about`} />} name={TabName.About} />
            <Tab title={<I18n k={`${T_PREFIX}.tabs.services`} />} name={TabName.Services} />
            <Tab
                className={withHiddenClass(!showWorkExperience)}
                title={<I18n k={`${T_PREFIX}.tabs.work_experience`} />}
                name={TabName.WorkExperience}
            />
            <Tab
                className={withHiddenClass(!showPortfolio)}
                title={<I18n k={`${T_PREFIX}.tabs.portfolio`} />}
                name={TabName.Portfolio}
            />
            <Tab
                className={withHiddenClass(!hasReviews)}
                title={<I18n k={`${T_PREFIX}.tabs.reviews`} />}
                name={TabName.Reviews}
            />
            <Tab
                className={withHiddenClass(!hasTestimonials)}
                title={<I18n k={`${T_PREFIX}.tabs.recommendations`} />}
                name={TabName.Testimonials}
            />
        </TabGroup>
    );

    return (
        <div className={classnames(styles.stickyHeader, headerClasses)} ref={headerRef}>
            <Section className={classnames(styles.sectionContainer, containerClasses, 'p-b-0')}>
                <Grid theme={Grid.THEMES.TWELVE}>
                    <Col md={7} className="flex flex-items-center">
                        <div className={classnames(styles.mdVisible, 'm-r-24')}>
                            <div className={styles.avatarWrapper}>
                                {isPro ? (
                                    <ProAvatar user={user} />
                                ) : (
                                    <Avatar
                                        size={56}
                                        username={user.name}
                                        src={user.profileImageUrl}
                                        isOnline={isOnline}
                                        showOnlineIndicator={isOnline}
                                        onlineIndicatorSize={8}
                                    />
                                )}
                                {worldCupWinnerBadgeImageUrl && (
                                    <img className={styles.cornerBadge} src={worldCupWinnerBadgeImageUrl} alt="" />
                                )}
                            </div>
                        </div>
                        <Stack direction="column" gap="2">
                            <ProfileBadgeRatings
                                usernameClassName="tbody-4"
                                displayName={user?.profile?.displayName}
                                onRatingsClick={onRatingsClick}
                                source={SOURCES.stickyHeader}
                                displayNameOnly
                                showSellerLevelNewBadges={inQualasNewLevelSystemRollout}
                            />
                            <div className={classnames(styles.mdVisible, 'co-text-darker')}>
                                <ProUserInfoContainer timeZone={sellerTimeZone} isOnline={isOnline} />
                            </div>
                        </Stack>
                    </Col>
                    <Col className={styles.mdVisible} md={5}>
                        <ButtonsGroup className={classnames(styles.buttonGroup, 'flex-end m-t-6')}>
                            <ContactButton
                                username={user.name}
                                source={SOURCES.stickyHeader}
                                size="lg"
                                className={styles.contactButton}
                                renderDropdown
                            />
                            <DotsMenuButton
                                user={user}
                                isLoggedIn={isLoggedIn}
                                inStickyHeader
                                source={SOURCES.stickyHeader}
                            />
                        </ButtonsGroup>
                        <Stack marginTop="2" color="grey_900" justifyContent="flexEnd">
                            <RespondTimeInfo respondHours={seller.responseTime?.inHours} />
                        </Stack>
                    </Col>
                </Grid>
                <div className="sticky-header-tabs">
                    {isExperiential ? experientialTabGroup : nonExperientialTabGroup}
                </div>
            </Section>
            <div className={classnames(styles.shadowBorder, containerClasses, 'box-shadow-z2')} />
        </div>
    );
};

StickyHeader.TabName = TabName;

const SafeStickyHeader = SafeWrapper(StickyHeader, 'StickyHeader', { TabName });

export { SafeStickyHeader as StickyHeader };
